import { capitalize } from "lodash";
import * as React from "react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/core/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useBusiness } from "@/core/business";
import { cn } from "@/core/utils/utils";
import {
  SIGNUP_SOURCE_CLASS_NAMES,
  SIGNUP_SOURCE_HUMAN_READABLE_NAMES,
} from "@/referral-sources/constants";
import { SignupSource, UserSource } from "@/referral-sources/referral-source";
import { isTech } from "@/user/utils";

interface AttributionBadgeProps {
  icon?: React.ReactNode;
  signupSource: SignupSource;
  tooltipContent?: React.ReactNode;
  className?: string;
  section: "referrals" | "customers" | "text-messages";
}

const SECTION_TO_LABEL = {
  referrals: "referral",
  customers: "customer",
  "text-messages": "text message",
};

export const AttributionBadge: React.FC<AttributionBadgeProps> = ({
  icon,
  signupSource,
  tooltipContent,
  className,
  section,
}) => {
  const navigate = useNavigate();
  const { businessId } = useBusiness();
  const { updateSidebar } = useBusinessSettingsSidebar();

  const handleSignupSourceClick = useCallback(() => {
    if (signupSource?.discarded_at) return;

    if (signupSource?.class_name === SIGNUP_SOURCE_CLASS_NAMES.user) {
      const user = signupSource as UserSource;
      updateSidebar({
        mode: SIDEBAR_MODES.view,
        selectedItemId: user.id,
        isOpen: true,
        section: SIDEBAR_SECTIONS.users,
      });
      navigate(
        `/business-settings/${businessId}/users/${isTech(user.role) ? "technicians" : "web-app-users"}`,
      );
    } else {
      updateSidebar({
        mode: SIDEBAR_MODES.view,
        selectedItemId: signupSource.id,
        isOpen: true,
        section: SIDEBAR_SECTIONS.referralSources,
      });
      navigate(`/business-settings/${businessId}/referral-sources`);
    }
  }, [signupSource, updateSidebar, navigate, businessId]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (signupSource?.discarded_at) return;

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSignupSourceClick();
      }
    },
    [signupSource?.discarded_at, handleSignupSourceClick],
  );

  const defaultTooltipContent = useMemo(() => {
    const humanReadableSourceName = capitalize(
      SIGNUP_SOURCE_HUMAN_READABLE_NAMES[signupSource.class_name],
    );

    const label = SECTION_TO_LABEL[section];

    if (signupSource?.discarded_at) {
      return `${humanReadableSourceName} attributed to this ${label} (no longer active)`;
    }

    if (signupSource?.class_name === SIGNUP_SOURCE_CLASS_NAMES.user) {
      return `${humanReadableSourceName} attributed to this ${label}. Click to view user details.`;
    } else {
      return `${humanReadableSourceName} attributed to this ${label}. Click to view referral source details.`;
    }
  }, [signupSource]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="relative">
          <Badge
            variant="gray"
            className={cn(
              "gap-2",
              {
                "cursor-pointer": !signupSource?.discarded_at,
              },
              className,
            )}
            role={signupSource?.discarded_at ? undefined : "button"}
            tabIndex={signupSource?.discarded_at ? undefined : 0}
            aria-disabled={signupSource?.discarded_at ? true : undefined}
            onClick={
              signupSource?.discarded_at ? undefined : handleSignupSourceClick
            }
            onKeyDown={handleKeyDown}
          >
            {icon}
            <Typography variant="captionW1">
              {signupSource.display_identification}
            </Typography>
          </Badge>
        </div>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent || defaultTooltipContent}</TooltipContent>
    </Tooltip>
  );
};

AttributionBadge.displayName = "AttributionBadge";
